<template>
    <div
        id="issue-header"
        class="w-100"
        :class="{ 'header-collapsed': isHeaderCollapsed, 'editing-issue': editModeEnabled, 'is-inside-viewer': locationType == 'viewer' }"
    >
        <!-- Delete issue confirmation modal -->
        <ActionConfirmationModal ref="delete-issue-confirmation" :loading="isDeletingIssue" />

        <!-- Files preview modal -->
        <FilesPreviewModal ref="attachments-preview-modal" :attachments="selectedIssue.attachments" />

        <div class="top-buttons-grid">
            <!-- issue status component -->
            <div v-if="!editModeEnabled && !isBlacklisted && !isArchived" class="issue-status-radio">
                <div class="issue-status-radio-option right" :class="{ selected: selectedIssue.status == 'open' }" @click="updateIssueStatus('open')">
                    <icon name="exclamation-circle" color="white" scale="0.75" class="mr-1" />
                    <p>{{ $t('keyword23') }}</p>
                </div>
                <div
                    class="issue-status-radio-option right left"
                    :class="{ selected: selectedIssue.status == 'in_review' }"
                    @click="updateIssueStatus('in_review')"
                >
                    <icon name="ci-issue-viewed" color="white" scale="0.75" class="mr-1" />
                    <p>{{ $t('keyword24') }}</p>
                </div>
                <div
                    class="issue-status-radio-option left"
                    :class="{ selected: selectedIssue.status == 'closed', disabled: !iCanCloseTheIssue }"
                    @click="updateIssueStatus('closed')"
                >
                    <icon name="check-circle" color="white" scale="0.75" class="mr-1" />
                    <p>{{ $t('keyword25') }}</p>
                </div>
            </div>
            <div class="d-flex justify-content-end align-items-center h-100">
                <b-dropdown no-caret v-if="!editModeEnabled && locationType != 'viewer' && checkPermission() && !isBlacklisted && !isArchived">
                    <template v-slot:button-content>
                        <div class="h-100 d-flex align-items-center">
                            <icon name="ellipsis-v" scale="1" color="var(--secondary-3)" />
                        </div>
                    </template>
                    <b-dropdown-item @click="enterEditMode">Editar</b-dropdown-item>
                    <b-dropdown-item @click="requestDeleteIssue"><b class="text-danger">Excluir</b></b-dropdown-item>
                </b-dropdown>

                <div class="d-flex" v-else-if="editModeEnabled && locationType != 'viewer'">
                    <ci-button @click="exitEditMode" type="primary-2" :text="$t('cancel')" class="mr-3" />
                    <ci-button :loading="issueEditLoading" @click="updateIssue" :text="$t('keyword133')" />
                </div>
            </div>
            <div v-if="isHeaderCollapsed" class="d-flex align-items-center w-100 h-100">
                <p class="text-16">
                    <span class="text-16 text-weight-bold" :class="`issue-${selectedIssue.priority}`">#{{ selectedIssue.code }}</span>
                    <span :title="'#' + selectedIssue.code + ' - ' + selectedIssue.title" style="margin-left: 5px">{{
                        selectedIssue.title.length > 30 ? selectedIssue.title.substr(0, 30) + '...' : selectedIssue.title
                    }}</span>
                </p>
            </div>
        </div>
        <!-- adjust to height transition -->

        <!-- 360 VIEW TYPE -->
        <transition-group name="fade">
            <div :key="1" v-show="!isHeaderCollapsed" class="issue-content-flex">
                <div class="general-info">
                    <div class="title">
                        <p class="text-16 text-weight-bold">
                            <strong>#{{ selectedIssue.code }}</strong>
                            {{ selectedIssue.title }}
                        </p>
                    </div>
                    <div v-if="selectedIssue.attachments.length" class="attachments-button d-flex mt-1" @click="$refs['attachments-preview-modal'].show()">
                        <icon name="paperclip" scale="1" color="var(--secondary-3)" />

                        <p class="text-12 text-secondary-3 ml-2">{{ selectedIssue.attachments.length }} {{ ' ' + $t('keyword67') }}</p>
                    </div>
                    <p class="text-10 created-by">
                        {{ $t('keyword118') }}
                        <span class="text-accent">{{ getCreatedBy }}</span>
                        {{ getCreatedAtDiff }}
                    </p>
                    <div class="location" @click="goToLocation">
                        <icon name="eye" scale="0.75" color="var(--secondary-3)" />

                        <p class="text-10 ml-2 text-accent">
                            {{ getLocation }}
                        </p>
                    </div>
                </div>

                <div class="thumbnail-attachments">
                    <div class="thumbnail" :style="`background-image: url(${getThumbnail})`"></div>
                </div>

                <p class="description text-10">
                    {{ selectedIssue.text }}
                </p>

                <div class="tags" v-if="getTagsArray.length">
                    <p class="text-10 text-secondary-3">{{ $t('keyword82') }}:</p>
                    <b-avatar-group size="16px" variant="secondary-2">
                        <b-avatar
                            v-for="tag in getTagsArray.slice(0, 3)"
                            :key="tag.id"
                            :src="tag.user ? tag.user.image : null"
                            v-b-tooltip.hover
                            :text="getFullnameInitials(tag)"
                            :title="tag.user ? tag.user.name + ' ' + tag.user.surname : tag.email"
                        />
                        <b-avatar v-if="getTagsArray.length > 4" :text="'+' + (getTagsArray.length - 4)" v-b-tooltip.hover :title="getRemainingTags"></b-avatar>
                    </b-avatar-group>
                </div>

                <div :key="3" class='subjects' v-if='issueSubjects.length'>
                    <p class='text-10 text-secondary-3'>{{ $t('keyword122') }}:</p>
                    <div class='d-flex'>
                        <p v-for='(subject, i) of issueSubjects' v-b-tooltip.hover :title='subject.name' :key='i'
                        class='text-10 text-white'>
                        {{ subject.code }}
                        </p>
                    </div>
                </div>

                <div :key="4" v-if='getCauseOfIssueSelected != null' class='issue-cause d-flex align-items-center'>
                    <p class='text-10 text-secondary-3'>{{ $t('cause.singular') }}:</p>
                    <p class='text-10 text-weight-bold ml-1'>
                        {{ getCauseOfIssueSelected.label + ' - ' + getCauseOfIssueSelected.name }}
                    </p>
                </div>

                <div :key="5" v-if='getTypeOfIssueSelected != null' class='issue-type d-flex align-items-center'>
                    <p class='text-10 text-secondary-3'>{{ $t('type.singular') }}:</p>
                    <p class='text-10 text-weight-bold ml-1'>
                        {{ getTypeOfIssueSelected.label + ' - ' + getTypeOfIssueSelected.name }}
                    </p>
                </div>

                <div :key="6" v-if='selectedIssue.due_date' class='issue-due-date mb-3 d-flex align-items-center'>
                    <p class='text-10 text-secondary-3'>{{ $t('deadline') }}:</p>
                    <p :class="['text-10 text-weight-bold ml-1', isIssueLate && 'text-danger']">
                        {{ selectedIssue.due_date }}
                    </p>
                </div>

            </div>
        
            <div :key="2" v-if="!editModeEnabled" @click="toggleHeader" class="header-collapse-button d-flex justify-content-center align-items-center">
                <icon
                    :name="isHeaderCollapsed ? 'chevron-down' : 'chevron-up'"
                    :scale="locationType == 'viewer' ? '.6' : '.8'"
                    :color="locationType == 'viewer' ? 'var(--light)' : 'var(--accent)'"
                />
                <b-avatar v-if='getTagsArray.length > 4' :text="'+' + (getTagsArray.length - 4)" v-b-tooltip.hover
                        :title='getRemainingTags'></b-avatar>
            </div>
        </transition-group>
      </div>
</template>

<script>
import 'vue-awesome/icons/exclamation-circle'
import 'vue-awesome/icons/eye'
import 'vue-awesome/icons/check-circle'
import 'vue-awesome/icons/share-alt'
import 'vue-awesome/icons/print'
import 'vue-awesome/icons/chevron-down'
import 'vue-awesome/icons/chevron-up'
import 'vue-awesome/icons/sort-down'
import 'vue-awesome/icons/sort-up'
import 'vue-awesome/icons/search-plus'
import 'vue-awesome/icons/times'
import 'vue-awesome/icons/pencil-alt'
import 'vue-awesome/icons/chevron-left'
import 'vue-awesome/icons/chevron-right'
import 'vue-awesome/icons/download'
import 'vue-awesome/icons/street-view'
import 'vue-awesome/icons/layer-group'
import 'vue-awesome/icons/map'
import 'vue-awesome/icons/ellipsis-v'

import VueCustomScrollbar from 'vue-custom-scrollbar'
import ActionConfirmationModal from '@/components/Utils/Modals/ActionConfirmationModal'
import FilesPreviewModal from '@/components/Utils/FilesPreviewModal'

import CustomFileDropzone from '@/components/Utils/CustomFileDropzone'

import CINUtils from '@/services/Utils'

import { mapState } from 'vuex'

// ===============
// JSON data
// ===============
import getIssueOptions from '@/data/getIssueOptions'
import causes from '@/data/filters/causes'
import types from '@/data/filters/types'
import subjects from '@/data/filters/subjects'

export default {
  components: {
    VueCustomScrollbar,
    ActionConfirmationModal,
    FilesPreviewModal,
    CustomFileDropzone,
  },
  props: {
    selectedIssue: Object,
    responsiblesOptions: {
      type: Array,
    },
    subjectsOptions: {
      type: Array,
    },
    members: Array,
    locationType: String,
    editModeEnabled: {
      type: Boolean,
      default: false,
    },
    isBlacklisted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      causes,
      types,
      subjects,
      isHeaderCollapsed: false,
      isLoadingPDF: false,
      isDeletingIssue: false,
      isThumbnailExpanded: false,
      isDropdownOpen: false,
      selectedResponsibles: [],
      selectedSubjects: [],
      issueSubjects: [],
      toEditIssue: {
        title: '',
        text: '',
        tags: [],
        subjects: [],
        type: null,
        cause: null,
        due_date: '',
        priority: '',
        attachment_thumbnail: null,
      },
      newThumb: null,
      issueEditLoading: false,
      downloadingFile: false,
      causesTreeSelectData: [],
      typesTreeSelectData: [],
    }
  },

    watch: {
        selectedIssue() {
            if (this.selectedIssue.id != -1) {
                this.issueSubjects = []
                this.selectedIssue.subjects.forEach((subject) => {
                    this.issueSubjects.push(subject)
                })
            }
        },
    },
    computed: {
        ...mapState(['user', 'sidebar']),
        getCauseOfIssueSelected() {
            let cause = null
            this.causes.forEach((c) => {
                c.options.forEach((_) => {
                    if (_.id == this.selectedIssue.cause) cause = _
                })
            })
            return cause
        },
        getTypeOfIssueSelected() {
            let type = null
            this.types.forEach((c) => {
                c.options.forEach((_) => {
                    if (_.id == this.selectedIssue.type) type = _
                })
            })
            return type
        },
        isArchived() {
            return false
        },
        getCreatedBy() {
            return this.selectedIssue.created_by.name + ' ' + this.selectedIssue.created_by.surname
        },
        getCreatedAtDiff() {
            return this.$t('keyword158') + ' ' + this.selectedIssue.created_at
        },
        getLocation() {
            if (this.selectedIssue.floorplan.map) {
                return this.$t('keyword49') + '/' + this.selectedIssue.viewpoint.name
            } else if (this.selectedIssue.sector) {
                return this.selectedIssue.sector.name + ' / ' + this.selectedIssue.floorplan.name + ' / ' + this.selectedIssue.viewpoint.name
            } else {
                return this.selectedIssue.floorplan.name + ' / ' + this.selectedIssue.viewpoint.name
            }
        },
        getRemainingTags() {
            let tagsArray = []
            let remainingMembersArray = []
            let remainingMembers = []
            this.selectedIssue.tags.forEach((tag) => {
                tag.members.forEach((member) => {
                    tagsArray.push(member)
                })
            })
            remainingMembersArray = tagsArray.slice(3)
            remainingMembersArray.forEach((member) => {
                if (member.user) {
                    remainingMembers.push(member.user.name + ' ' + member.user.surname)
                }
            })
            return remainingMembers.join(', ')
        },
        getTagsArray() {
            let tagsArray = []
            this.selectedIssue.tags.forEach((tag) => {
                tag.members.forEach((member) => {
                    tagsArray.push(member)
                })
            })
            return tagsArray
        },

        getThumbnail() {
            if (this.newThumb) {
                return this.newThumb
            }

            if (this.editModeEnabled) {
                return this.toEditIssue.attachment_thumbnail
                    ? this.toEditIssue.attachment_thumbnail
                    : this.toEditIssue.thumbnail
                    ? this.toEditIssue.thumbnail
                    : ''
            }

            return this.selectedIssue.attachment_thumbnail
                ? this.selectedIssue.attachment_thumbnail
                : this.selectedIssue.thumbnail
                ? this.selectedIssue.thumbnail
                : ''
        },

        getSubjects() {
            return this.issueSubjects.map((sub) => sub.code).join(',')
        },
        attachments() {
            return this.selectedIssue.attachments && this.selectedIssue.attachments.length > 0 ? this.selectedIssue.attachments : []
        },

        isIssueLate() {
            if (!this.selectedIssue.due_date) {
                return false
            }
            let now = this.$moment()
            let dueDate = this.$moment(this.selectedIssue.due_date, 'DD/MM/YYYY')

            return dueDate.diff(now) < 0
        },

        iCanCloseTheIssue() {
            return true
        },
    },
    methods: {
        requestDeleteIssue() {
            this.$refs['delete-issue-confirmation'].show(
                this.$t('Components_Viewer_DeleteHS_Issue_Title'),
                this.$t('Components_Viewer_DeleteHS_Issue_Description'),
                (confirmed) => {
                    if (confirmed) {
                        this.deleteIssue()
                    }
                }
            )
        },
        /**
         * Summary.
         * Request to delete an issue.
         *
         * @param {Number} id
         */
        deleteIssue() {
            this.isDeletingIssue = true

            this.$apollo
                .mutate({
                    mutation: deleteIssue,
                    variables: {
                        id: this.selectedIssue.id,
                    },
                })
                .then(() => {
                    this.isDeletingIssue = false
                    this.$refs['delete-issue-confirmation'].hide()

                    this.$emit('onIssueDeleted', this.selectedIssue.id)
                })
                .catch((e) => {
                    this.isDeletingIssue = false
                    this.$notify({
                        group: 'foo',
                        type: 'error',
                        title: this.$t('Projects_notify_error_title3'),
                        text: this.$t('Projects_notify_error_text37'),
                    })
                })
        },
        checkPermission() {
            return true
        },
        getFullnameInitials(tag) {
            return tag.user ? tag.user.name.slice(0, 1) + ' ' + tag.user.surname.slice(0, 1) : tag.email.slice(0, 1)
        },
        getIssueOptionsQuery() {
            let data = getIssueOptions.data
            let causes = data.getIssueOptions.causes
            let types = data.getIssueOptions.types

            this.causesTreeSelectData = []
            causes.forEach((c) => {
                c.options.forEach((o) => {
                    this.causesTreeSelectData.push({
                        id: o.id,
                        label: o.label + ' - ' + o.name,
                    })
                })
            })

            this.typesTreeSelectData = []
            types.forEach((t) => {
                t.options.forEach((o) => {
                    this.typesTreeSelectData.push({
                        id: o.id,
                        label: o.label + ' - ' + o.name,
                    })
                })
            })
        },
        toggleHeader() {
            this.isHeaderCollapsed = !this.isHeaderCollapsed
            this.$emit('collapsed', this.isHeaderCollapsed)
        },
        formatEditTags() {
            return this.toEditIssue.tags.flatMap((tag) => {
                let members = []

                if (typeof tag == 'string') {
                    let option = this.responsiblesOptions.find((ro) => ro.id == tag)
                    option.children.forEach((child) => {
                        members.push(this.members.find((member) => member.id == child.id).email)
                    })
                } else {
                    members = [this.members.find((member) => member.id == tag).email]
                }

                return members
            })
        },
        goToLocation() {},
        async updateIssue() {
            this.issueEditLoading = true
            if (this.newThumb) {
                try {
                    let attThumbUrl = await this.$Firebase.uploadBase64(this.newThumb, 'image/png', 'ISSUE_THUMB')
                    this.toEditIssue.attachment_thumbnail = attThumbUrl
                } catch {
                    this.toEditIssue.attachment_thumbnail = null
                }
            }
        },
        enterEditMode() {
            this.isHeaderCollapsed = false
            this.toEditIssue.title = this.selectedIssue.title
            this.toEditIssue.text = this.selectedIssue.text
            if (this.selectedIssue.due_date == null) {
                this.toEditIssue.due_date = null
            } else {
                let splitDate = this.selectedIssue.due_date.split('/')
                this.toEditIssue.due_date = splitDate[2] + '-' + splitDate[1] + '-' + splitDate[0]
            }
            if (this.selectedIssue.type != null) {
                this.toEditIssue.type = this.selectedIssue.type.id.toString()
            }
            if (this.selectedIssue.cause != null) {
                this.toEditIssue.cause = this.selectedIssue.cause.id.toString()
            }
            this.toEditIssue.priority = this.selectedIssue.priority
            this.toEditIssue.thumbnail = this.selectedIssue.thumbnail
            this.toEditIssue.attachment_thumbnail = this.selectedIssue.attachment_thumbnail
            this.selectedIssue.tags.forEach((tag) => tag.members.forEach((member) => this.toEditIssue.tags.push(member.id)))
            this.selectedIssue.subjects.forEach((subject) => this.toEditIssue.subjects.push(subject.id))
            this.$emit('editing', true)
        },
        exitEditMode() {
            this.$emit('editing', false)
            this.resetToEditIssue()
        },
        resetToEditIssue() {
            this.toEditIssue.title = ''
            this.toEditIssue.text = ''
            this.toEditIssue.tags = []
            this.toEditIssue.subjects = []
            this.newThumb = null
        },
        updateIssueStatus(status) {
            if (status == 'closed' && !this.iCanCloseTheIssue) {
                return
            }

            let old_status = this.selectedIssue.status
            this.$emit('onStatusChange', { issue: this.selectedIssue, status, old_status })
        },
        expandThumbnail() {
            this.isThumbnailExpanded = true
        },
        editIssueThumb() {
            this.$refs['attachment-thumb'].$el.click()
        },
        async saveThumb(file) {
            if (file.type.includes('image')) {
                this.newThumb = await CINUtils.toBase64(file)
            }
        },
    },
    mounted() {
        this.getIssueOptionsQuery()
        this.selectedIssue.subjects.forEach((subject) => {
            this.issueSubjects.push(subject)
        })
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/components/Issues/issue-header.scss';

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
